import { Badge, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Switch } from '@repletoryreach/sdk-ui/inputs';
import type { admin } from '@repletoryreach/sdk-api';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { formatInteger, formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { priceLine, regionText, usesPlatformModel, type ModelPrice } from '../aiModels';

const MUTED = 'var(--rr-color-text-muted)';

/** The two things a business is told about a model before it sends anything: pictures, and where it runs. */
export function ModelFlags({ model }: { model: admin.AdminAiModel }) {
  const t = useT();
  if (!model.accepts_images && !model.outside_south_africa) return null;
  return (
    <Group gap={4} wrap="wrap">
      {model.accepts_images && <Badge size="xs" variant="light" color="blue">{t('billing.aiModels.flag.pictures')}</Badge>}
      {model.outside_south_africa && <Badge size="xs" variant="light" color="orange">{t('billing.aiModels.flag.outside')}</Badge>}
    </Group>
  );
}

/** The name a business sees, with the code it sends in the API beneath it. */
export function ModelNameCell({ model }: { model: admin.AdminAiModel }) {
  return (
    <Stack gap={4}>
      <TwoLineCell primary={model.name} secondary={<code>{model.code}</code>} title={model.description ?? undefined} />
      <ModelFlags model={model} />
    </Stack>
  );
}

/** The model id (or the platform's own text model) over the region it runs in. */
export function RunsOnCell({ model }: { model: admin.AdminAiModel }) {
  const t = useT();
  return (
    <TwoLineCell
      primary={usesPlatformModel(model) ? t('billing.aiModels.modelId.platform') : <code>{model.model_id}</code>}
      secondary={regionText(t, model)}
      title={usesPlatformModel(model) ? undefined : model.model_id}
    />
  );
}

/**
 * What one unit costs on the Free plan, from the price book. A SKU with no rate in force is said out loud, since a
 * model switched on without one cannot be charged for.
 */
export function PriceCell({ price, ratesFailed }: { price: ModelPrice; ratesFailed: boolean }) {
  const t = useT();
  if (ratesFailed) return <Text role="bodySmall" c={MUTED}>–</Text>;
  if (price.unpriced) return <Text role="bodySmall" c="var(--rr-color-error)">{t('billing.aiModels.price.none')}</Text>;
  const list = priceLine(t, price, 'list');
  const cost = priceLine(t, price, 'cost');
  return (
    <Stack gap={2}>
      {list && (
        <Text role="bodySmall" className="rr-billing-money" style={{ whiteSpace: 'normal' }} title={cost ? t('billing.aiModels.price.costTitle', { cost }) : undefined}>
          {list}
        </Text>
      )}
      {price.missing.length > 0 && (
        <Text role="caption" c="var(--rr-color-error)">{t('billing.aiModels.price.missing', { skus: price.missing.join(', ') })}</Text>
      )}
    </Stack>
  );
}

/** Calls that finished in the last 30 days, and what they charged to businesses' credits. */
export function UsageCell({ model }: { model: admin.AdminAiModel }) {
  const t = useT();
  return (
    <TwoLineCell
      primary={t('billing.aiModels.usage.calls', { count: formatInteger(model.calls_last30_days) })}
      secondary={formatMinor(model.charged_last30_days_minor, 'ZAR')}
    />
  );
}

/** On or off. The page confirms before it changes anything. */
export function ModelSwitch({ model, busy, onToggle }: { model: admin.AdminAiModel; busy: boolean; onToggle: (m: admin.AdminAiModel, on: boolean) => void }) {
  const t = useT();
  return (
    <Switch
      size="sm"
      checked={model.enabled}
      disabled={busy}
      onChange={(on) => onToggle(model, on)}
      label={model.enabled ? t('billing.aiModels.state.on') : t('billing.aiModels.state.off')}
      aria-label={t('billing.aiModels.switchLabel', { name: model.name })}
    />
  );
}

/** What the last Test in this visit said, so a model is not switched on on the strength of a test nobody ran. */
export function TestOutcome({ result }: { result: admin.AiModelTest | undefined }) {
  const t = useT();
  if (!result) return null;
  if (!result.ok) return <Text role="caption" c="var(--rr-color-error)">{t('billing.aiModels.outcome.failed')}</Text>;
  if (result.stub) return <Text role="caption" c="var(--rr-color-warning)">{t('billing.aiModels.outcome.stub')}</Text>;
  return <Text role="caption" c="var(--rr-color-success)">{t('billing.aiModels.outcome.ok', { ms: formatInteger(result.milliseconds) })}</Text>;
}
